import * as Sentry from '@sentry/react';
import { useTracking } from 'shared/providers/TrackingProvider';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import React, { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import {
  StripeDisputeStatus,
  StripeRefundReason as StripeRefundReason,
  useGetPaymentReportStripePaymentIntentQuery,
  useGetStripeDisputesByPaymentIntentQuery,
  useRefundStripePaymentIntentMutation,
} from 'shared/types/graphql';
import { capitalize, getMonetaryDisplayValue } from 'shared/utils';
import { getPaymentIntentDetails } from 'shared/utils/stripe';
import CurrencyInput from '../CurrencyInput';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Box, Divider, Flex } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { SidePanelInvoicingEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IRefundModalProps {
  paymentIntentId: string;
  channelId?: string;
  refetchStripePaymentIntent?: () => void;
  setIsProcessing?: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  onClose: () => void;
}

const RefundModal = ({
  isOpen,
  onClose,
  channelId,
  paymentIntentId,
  setIsProcessing,
  refetchStripePaymentIntent,
}: IRefundModalProps): JSX.Element | null => {
  const { trackEvent } = useTracking();
  const toast = useToast();

  const { data: paymentIntentData } = useGetPaymentReportStripePaymentIntentQuery({
    variables: {
      where: {
        id: paymentIntentId,
      },
    },
    skip: !paymentIntentId,
  });

  const { data: disputesData } = useGetStripeDisputesByPaymentIntentQuery({
    variables: {
      where: {
        stripePaymentIntentId: {
          equals: paymentIntentId,
        },
        status: {
          in: [
            StripeDisputeStatus.NeedsResponse,
            StripeDisputeStatus.WarningNeedsResponse,
            StripeDisputeStatus.UnderReview,
            StripeDisputeStatus.WarningUnderReview,
          ],
        },
      },
    },
    skip: !paymentIntentId,
  });

  const hasActiveDisputes = useMemo(
    () => (disputesData?.findManyStripeDispute?.length || []) > 0,
    [disputesData?.findManyStripeDispute?.length],
  );

  const paymentIntent = useMemo(() => paymentIntentData?.findUniqueStripePaymentIntent, [paymentIntentData]);

  const { refundedTotal, totalRefundedClientFees, totalInitialRemainingBalance } =
    getPaymentIntentDetails(paymentIntent);
  const clientFullName = [paymentIntent?.clinicPetParent?.firstName, paymentIntent?.clinicPetParent?.lastName].join(
    ' ',
  );

  const [amountToRefund, setAmountToRefund] = useState<string>();
  const numericAmountToRefund = useMemo(
    () => parseInt(((parseFloat(amountToRefund || '') || 0) * 100).toFixed(0)),
    [amountToRefund],
  );
  const [refundReason, setRefundReason] = useState<StripeRefundReason>();
  const [refundReasonMessage, setRefundReasonMessage] = useState<string>('');
  const [refunderInitials, setRefunderInitials] = useState<string>('');
  const [refundReasonValidationMessage, setRefundReasonValidationMessage] = useState('');
  const [amountToRefundValidationMessage, setAmountToRefundValidationMessage] = useState('');
  const subtotalInitialRemainingBalance: number = useMemo(
    () => (paymentIntent?.amount ? paymentIntent?.amount - refundedTotal : 0),
    [paymentIntent, refundedTotal],
  );

  const remainingBalanceClientFee = useMemo(
    () =>
      paymentIntent?.clientServiceFee && paymentIntent?.clientServiceFeePercent
        ? paymentIntent.clientServiceFee -
          totalRefundedClientFees -
          Math.round(numericAmountToRefund * paymentIntent.clientServiceFeePercent)
        : 0,

    [paymentIntent, totalRefundedClientFees, numericAmountToRefund],
  );

  const [refundStripePayment, { loading: isRefundProcessing }] = useRefundStripePaymentIntentMutation({
    errorPolicy: GraphQLErrorPolicies.All,
  });

  const refundReasonOptions: SelectOptionProps[] = useMemo(
    () => [
      ...Object.values(StripeRefundReason).map((value) => ({
        label: capitalize(value).replace(/_/g, ' '),
        value,
      })),
    ],
    [],
  );

  const validateRefundAmount = useCallback(
    (amount: number): boolean => {
      if (!amount) {
        setAmountToRefundValidationMessage('Please provide a refund amount.');
        return false;
      }

      if (!(amount > 0 && amount <= subtotalInitialRemainingBalance)) {
        setAmountToRefundValidationMessage(
          `Please provide a refund amount up to $${getMonetaryDisplayValue(subtotalInitialRemainingBalance)}.`,
        );
        return false;
      }

      setAmountToRefundValidationMessage('');
      return true;
    },
    [subtotalInitialRemainingBalance],
  );

  const handleChangeRefundAmount = useCallback(
    (value: string): void => {
      const numericValue = parseInt(((parseFloat(value || '') || 0) * 100).toFixed(0));
      setAmountToRefund(value);
      validateRefundAmount(numericValue);
    },
    [validateRefundAmount],
  );

  const handleChangeRefunderInitials = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value?.length <= 3) {
      setRefunderInitials(e.target.value);
    }
  }, []);

  const handleChangeRefundReasonMessage = useCallback((e: ChangeEvent<HTMLTextAreaElement>): void => {
    setRefundReasonMessage(e.target.value);
    if (e.target.value) {
      setRefundReasonValidationMessage('');
    }
  }, []);

  const remainingBalanceSubtotal = useMemo(() => {
    return paymentIntent?.amount ? paymentIntent?.amount - refundedTotal - numericAmountToRefund : 0;
  }, [numericAmountToRefund, paymentIntent, refundedTotal]);

  const remainingBalanceTotal = useMemo(
    () => remainingBalanceClientFee + remainingBalanceSubtotal,
    [remainingBalanceClientFee, remainingBalanceSubtotal],
  );

  const validateRefundForm = useCallback((): boolean => {
    if (!paymentIntent?.total) return false;

    if (!refundReason) {
      setRefundReasonValidationMessage('Please select a reason for this refund.');
      return false;
    }
    if (refundReason === StripeRefundReason.Other && !refundReasonMessage) {
      setRefundReasonValidationMessage('Please describe the reason for this refund.');
      return false;
    }

    return validateRefundAmount(numericAmountToRefund);
  }, [paymentIntent?.total, refundReason, refundReasonMessage, validateRefundAmount, numericAmountToRefund]);

  const handleModalClose = useCallback((): void => {
    // Always reset the modal state, even on errors
    setAmountToRefund('0');
    setRefundReason(undefined);
    setRefundReasonMessage('');
    setRefunderInitials('');
    setRefundReasonValidationMessage('');
    setAmountToRefundValidationMessage('');

    if (setIsProcessing) setIsProcessing(false);

    onClose();
  }, [onClose, setIsProcessing]);

  const onRefundClicked = useCallback(async (): Promise<void> => {
    if (!paymentIntent?.stripeId) {
      handleModalClose();
      toast({
        title: 'This transaction is missing a Stripe ID. The refund could not be processed successfully.',
        status: 'error',
      });
      return;
    }

    if (!validateRefundForm()) return;

    try {
      if (setIsProcessing) setIsProcessing(true);
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = {
        amount: numericAmountToRefund,
        reason: refundReason as StripeRefundReason,
        reasonOther: refundReasonMessage || undefined,
        issuerInitials: refunderInitials || undefined,
      };
      if (channelId) data.channelId = channelId;
      const response = await refundStripePayment({
        variables: {
          where: {
            id: paymentIntent.id,
          },
          data,
        },
      });

      // If we're missing a response or have errors, throw the errors if one exists
      if (!response.data?.refundStripePaymentIntent2 || response.errors?.length) {
        throw new Error(response.errors?.[0].message ?? 'Error processing refund');
      }

      trackEvent(SidePanelInvoicingEvents.RefundInitiated);
      toast({
        status: 'success',
        title: `Invoice sucessfully refunded ${clientFullName ? 'to ' + clientFullName : ''} for $${(
          numericAmountToRefund / 100
        ).toFixed(2)}`,
      });
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      toast({
        status: 'error',
        title:
          'An error occurred while refunding this transaction. Please wait a few minutes and try again or contact our support team.',
      });
    } finally {
      // We want to refetch on both successful and failures (in case this is a network failure and we actually did issue a refund, we should load that)
      if (refetchStripePaymentIntent) {
        refetchStripePaymentIntent();
      }

      handleModalClose();
    }
  }, [
    paymentIntent,
    validateRefundForm,
    handleModalClose,
    toast,
    setIsProcessing,
    numericAmountToRefund,
    refundReason,
    refundReasonMessage,
    refunderInitials,
    channelId,
    refundStripePayment,
    trackEvent,
    clientFullName,
    refetchStripePaymentIntent,
  ]);

  return (
    <Modal id="refund-modal" isOpen={isOpen} onClose={handleModalClose} closeOnOverlayClick={false} size="md">
      <ModalHeader>Issue Refund</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box className="RefundModal__RefundContainer">
          <Box p={4} bgColor="background.subtle" borderRadius="md">
            <Flex justifyContent="space-between" mb={2}>
              <Text as="p" fontWeight="semibold">
                Transaction Balance
              </Text>
            </Flex>
            <Box mt={2}>
              <Flex justifyContent="space-between">
                <Text as="p" ml={1} size="sm">
                  Subtotal:
                </Text>
                <Text as="p" ml={1} size="sm">
                  {getMonetaryDisplayValue(subtotalInitialRemainingBalance, true)}
                </Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text as="p" ml={1} size="sm">
                  Client Service Fee:
                </Text>
                <Text as="p" ml={1} size="sm">
                  {getMonetaryDisplayValue((paymentIntent?.clientServiceFee || 0) - totalRefundedClientFees, true)}
                </Text>
              </Flex>
              <Divider my={2} />
              <Flex justifyContent="space-between">
                <Text as="p" ml={1} size="sm">
                  Total:
                </Text>
                <Text as="p" ml={1} size="sm">
                  ${totalInitialRemainingBalance}
                </Text>
              </Flex>
            </Box>
          </Box>
          <Box mt={4}>
            <Select
              name="refundReason"
              label="Reason for Refund"
              labelStyle={{ fontWeight: 'semibold' }}
              placeholder="Select a reason"
              options={refundReasonOptions}
              value={refundReason}
              isRequired
              isInvalid={!!refundReasonValidationMessage.length}
              errorText={refundReasonValidationMessage}
              listProps={{
                onSelect: (selectedItem): void => {
                  const value = selectedItem.value as StripeRefundReason;
                  setRefundReason(value);
                  setRefundReasonValidationMessage('');
                },
              }}
            />
            {refundReason === StripeRefundReason.Other && (
              <Textarea
                label="Refund Details"
                placeholder="Add more details about this refund."
                labelStyle={{ fontWeight: 'semibold' }}
                isRequired
                value={refundReasonMessage}
                onChange={handleChangeRefundReasonMessage}
                isInvalid={!!refundReasonValidationMessage.length}
                errorText={refundReasonValidationMessage}
                rows={3}
                formControlStyle={{ mt: 4 }}
                resize="none"
              />
            )}
            <Box mt={4}>
              <CurrencyInput
                label="Amount to Refund"
                value={amountToRefund ? String(amountToRefund) : ''}
                onChange={handleChangeRefundAmount}
                isRequired
                errorText={amountToRefundValidationMessage}
                isInvalid={!!amountToRefundValidationMessage.length}
              />
            </Box>
          </Box>
          <Box p={4} my={4} bgColor="background.info" borderRadius="md">
            <Text as="p" fontWeight="semibold" mb={2}>
              Remaining Balance After Refund
            </Text>
            <Flex justifyContent="space-between">
              <Text as="p" size="sm">
                Subtotal:
              </Text>
              <Text as="p" size="sm">
                {getMonetaryDisplayValue(remainingBalanceSubtotal, true)}
              </Text>
            </Flex>
            <Flex justifyContent="space-between">
              <Text as="p" size="sm">
                Client Service Fee:
              </Text>
              <Text as="p" size="sm">
                {getMonetaryDisplayValue(remainingBalanceClientFee, true)}
              </Text>
            </Flex>
            <Divider my={2} borderColor="border.subtle" />
            <Flex justifyContent="space-between">
              <Text as="p" size="sm">
                Total:
              </Text>
              <Text as="p" size="sm">
                {getMonetaryDisplayValue(remainingBalanceTotal, true)}
              </Text>
            </Flex>
          </Box>
          <TextInput
            label="Your Initials"
            labelStyle={{ fontWeight: 'semibold' }}
            value={refunderInitials}
            onChange={handleChangeRefunderInitials}
            formControlStyle={{ mb: 4 }}
            maxW="80px"
          />
          {hasActiveDisputes && (
            <Alert
              title="Refund not available"
              description="This transaction has an active dispute. See the Disputes tab for more information."
              status="warning"
              hideCloseButton
              mb={3}
            />
          )}
        </Box>
      </ModalBody>
      <ModalFooterButtons
        submitText="Issue Refund"
        onSubmit={onRefundClicked}
        isLoading={isRefundProcessing}
        isDisabled={hasActiveDisputes}
      />
    </Modal>
  );
};

export default RefundModal;
